import { useState, useMemo } from "react";

const allCities = [
  { name: "Salt Lake City", tagline: "State capital expedited passport services", highlights: ["Downtown & Capitol area coverage", "Serving Murray, West Valley City, South Salt Lake"], badge: "Coming Soon", status: "coming" },
  { name: "Provo", tagline: "Utah County expedited passport processing", highlights: ["BYU campus & downtown coverage", "Serving Orem, Springville, Spanish Fork"], badge: "Coming Soon", status: "coming" },
  { name: "Ogden", tagline: "Northern Utah expedited passport services", highlights: ["Downtown & Historic 25th Street coverage", "Serving Layton, Roy, Clearfield, Bountiful"], badge: "Coming Soon", status: "coming" },
  { name: "St. George", tagline: "Southern Utah expedited passport processing", highlights: ["Downtown & Red Cliffs coverage", "Serving Washington, Hurricane, Cedar City"], badge: "Coming Soon", status: "coming" },
  { name: "Orem", tagline: "Utah Valley expedited passport services", highlights: ["University Parkway & State Street coverage", "Serving Provo, Lindon, Pleasant Grove"], badge: "Coming Soon", status: "coming" },
  { name: "West Valley City", tagline: "Salt Lake County expedited passport processing", highlights: ["Downtown & Valley Fair coverage", "Serving Salt Lake City, Taylorsville, Kearns"], badge: "Coming Soon", status: "coming" },
  { name: "Sandy", tagline: "Salt Lake suburb expedited passport services", highlights: ["Downtown & South Towne coverage", "Serving Draper, Cottonwood Heights, Alta"], badge: "Coming Soon", status: "coming" },
  { name: "Layton", tagline: "Davis County expedited passport processing", highlights: ["Downtown & Layton Hills coverage", "Serving Ogden, Clearfield, Bountiful, Syracuse"], badge: "Coming Soon", status: "coming" },
  { name: "Logan", tagline: "Cache Valley expedited passport services", highlights: ["USU campus & downtown coverage", "Serving North Logan, Smithfield, Hyrum"], badge: "Coming Soon", status: "coming" },
  { name: "Moab", tagline: "Southeast Utah expedited passport processing", highlights: ["Downtown & Arches gateway coverage", "Serving Monticello, Blanding, Green River"], badge: "Coming Soon", status: "coming" },
  { name: "Park City", tagline: "Summit County expedited passport services", highlights: ["Main Street & resort area coverage", "Serving Heber City, Kamas, Midway"], badge: "Coming Soon", status: "coming" },
  { name: "Cedar City", tagline: "Iron County expedited passport processing", highlights: ["SUU campus & downtown coverage", "Serving Enoch, Parowan, Panguitch"], badge: "Coming Soon", status: "coming" },
];

const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

export default function UTCitiesDirectory() {
  const [activeLetter, setActiveLetter] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  const lettersWithCities = useMemo(() => {
    const set = new Set<string>();
    allCities.forEach((c) => set.add(c.name[0].toUpperCase()));
    return set;
  }, []);

  const filteredCities = useMemo(() => {
    let result = allCities;
    if (activeLetter) {
      result = result.filter((c) => c.name[0].toUpperCase() === activeLetter);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.tagline.toLowerCase().includes(q) ||
          c.highlights.some((h) => h.toLowerCase().includes(q))
      );
    }
    return result;
  }, [activeLetter, searchQuery]);

  const liveCount = allCities.filter((c) => c.status === "live").length;
  const comingCount = allCities.filter((c) => c.status === "coming").length;

  return (
    <section id="cities" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-10">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">
            Utah Cities
          </p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Choose Your Utah Location
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed mb-2">
            Select your city to view local acceptance facilities, processing options, and
            expedited services tailored to your area.
          </p>
          <p className="text-gray-400 text-sm">
            <span className="text-[#1A365D] font-semibold">{liveCount}</span> cities live
            &nbsp;&middot;&nbsp;
            <span className="text-[#1A365D] font-semibold">{comingCount}</span> more coming soon
          </p>
        </div>

        <div className="max-w-xl mx-auto mb-6">
          <div className="relative">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
              <i className="ri-search-line text-gray-400"></i>
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                if (e.target.value.trim() && activeLetter) setActiveLetter(null);
              }}
              placeholder="Search cities..."
              className="w-full pl-11 pr-10 py-3 bg-white border border-gray-200 rounded-lg text-sm text-[#1A365D] placeholder:text-gray-400 focus:outline-none focus:border-[#F6C344] focus:ring-1 focus:ring-[#F6C344] transition-all"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600"
              >
                <i className="ri-close-line"></i>
              </button>
            )}
          </div>
        </div>

        <div className="max-w-4xl mx-auto mb-12">
          <div className="flex flex-wrap items-center justify-center gap-1.5 md:gap-2">
            <button
              onClick={() => setActiveLetter(null)}
              className={`px-3 py-1.5 rounded-md text-sm font-semibold transition-all whitespace-nowrap ${
                activeLetter === null
                  ? "bg-[#1A365D] text-white"
                  : "bg-white text-gray-500 hover:bg-[#EEF2F8] hover:text-[#1A365D] border border-gray-200"
              }`}
            >
              All
            </button>
            {LETTERS.map((letter) => {
              const hasCities = lettersWithCities.has(letter);
              const isActive = activeLetter === letter;
              return (
                <button
                  key={letter}
                  onClick={() => hasCities && setActiveLetter(isActive ? null : letter)}
                  disabled={!hasCities}
                  className={`w-8 h-8 md:w-9 md:h-9 rounded-md text-sm font-semibold flex items-center justify-center transition-all ${
                    isActive
                      ? "bg-[#1A365D] text-white"
                      : hasCities
                        ? "bg-white text-[#1A365D] hover:bg-[#EEF2F8] border border-gray-200 cursor-pointer"
                        : "bg-gray-100 text-gray-300 border border-gray-100 cursor-not-allowed"
                  }`}
                >
                  {letter}
                </button>
              );
            })}
          </div>
        </div>

        {(activeLetter || searchQuery) && (
          <div className="flex items-center justify-between max-w-7xl mb-6">
            <p className="text-sm text-gray-500">
              Showing{" "}
              <span className="font-semibold text-[#1A365D]">{filteredCities.length}</span>{" "}
              {filteredCities.length === 1 ? "city" : "cities"}
              {activeLetter && (
                <>
                  {" "}
                  starting with <span className="font-semibold text-[#1A365D]">{activeLetter}</span>
                </>
              )}
              {searchQuery && (
                <>
                  {" "}
                  matching <span className="font-semibold text-[#1A365D]">"{searchQuery}"</span>
                </>
              )}
            </p>
            <button
              onClick={() => {
                setActiveLetter(null);
                setSearchQuery("");
              }}
              className="text-sm text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors whitespace-nowrap"
            >
              Clear filters
            </button>
          </div>
        )}

        {filteredCities.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCities.map((city, i) => (
              <div
                key={i}
                className="group relative bg-white border border-dashed border-gray-300 rounded-lg p-7 flex flex-col"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-gray-100 flex-shrink-0">
                    <i className="ri-time-line text-gray-400 text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-400 text-lg">{city.name}</h3>
                    <span className="inline-block bg-gray-100 text-gray-500 text-xs font-semibold px-2 py-0.5 rounded-full mt-0.5">
                      Coming Soon
                    </span>
                  </div>
                </div>
                <p className="text-gray-400 text-sm leading-relaxed mb-4">{city.tagline}</p>
                <ul className="space-y-2 mb-6 flex-1">
                  {city.highlights.map((h, j) => (
                    <li key={j} className="flex items-center gap-2 text-sm text-gray-400">
                      <i className="ri-check-line text-gray-300 flex-shrink-0"></i>
                      {h}
                    </li>
                  ))}
                </ul>
                <div className="inline-flex items-center gap-1.5 text-gray-400 font-semibold text-sm mt-auto whitespace-nowrap">
                  Coming Soon <i className="ri-time-line"></i>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#EEF2F8] mx-auto mb-4">
              <i className="ri-search-line text-[#1A365D] text-2xl"></i>
            </div>
            <h3 className="font-bold text-[#1A365D] text-lg mb-2">No cities found</h3>
            <p className="text-gray-500 text-sm max-w-md mx-auto">
              Try a different search term or letter. We are always adding new Utah locations.
            </p>
            <button
              onClick={() => {
                setActiveLetter(null);
                setSearchQuery("");
              }}
              className="mt-4 inline-flex items-center gap-1.5 text-[#1A365D] hover:text-[#F6C344] font-semibold text-sm transition-colors whitespace-nowrap"
            >
              Show all cities <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        )}
      </div>
    </section>
  );
}